/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import JSZip from 'jszip';
import { useAppDispatch, useAppSelector } from '../../../helpers/hooks';
import { useWorkspaceSlice } from '../slice';
import {
  selectApiKeys,
  selectApiKeysLoading,
  selectNewApiKeySecret,
  selectProject,
} from '../slice/selectors';
import { exportsApi } from '../../../services/exportsApi';
import useNotificantionProvider from '../../Notifications/useNotificantionProvider';

interface DeliveryTabProps {
  projectId: string;
}

export default function DeliveryTab({ projectId }: DeliveryTabProps) {
  const dispatch = useAppDispatch();
  const { actions } = useWorkspaceSlice();
  const { setNotification } = useNotificantionProvider();

  const project = useAppSelector(selectProject);
  const apiKeys = useAppSelector(selectApiKeys);
  const loading = useAppSelector(selectApiKeysLoading);
  const newSecret = useAppSelector(selectNewApiKeySecret);

  const [keyName, setKeyName] = useState('');

  const [exportLanguage, setExportLanguage] = useState(
    project?.targetLanguages[0] ?? '',
  );
  const [format, setFormat] = useState<'nested' | 'flat'>('nested');
  const [includeUnapproved, setIncludeUnapproved] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [bundle, setBundle] = useState<string>('');

  useEffect(() => {
    dispatch(actions.fetchApiKeysRequest(projectId));
  }, [projectId]);

  const handleCreateKey = () => {
    if (keyName) {
      dispatch(actions.createApiKeyRequest({ projectId, name: keyName }));
      setKeyName('');
    }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    setNotification({ message: 'Copied to clipboard', type: 'success' });
  };

  const handleExport = async () => {
    if (!exportLanguage) return;
    setExporting(true);
    try {
      const data = await exportsApi.bundle(
        projectId,
        exportLanguage,
        format,
        includeUnapproved,
      );
      setBundle(JSON.stringify(data, null, 2));
    } catch (error) {
      setNotification({
        message: error instanceof Error ? error.message : 'Export failed',
        type: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadBundle = () => {
    downloadBlob(
      new Blob([bundle], { type: 'application/json' }),
      `${project?.slug || 'project'}-${exportLanguage}.json`,
    );
  };

  const handleDownloadZip = async () => {
    if (!project) return;
    // Source language (base strings) + every target language, de-duplicated.
    const languages = Array.from(
      new Set([project.sourceLanguage, ...project.targetLanguages]),
    );
    if (languages.length === 0) return;

    setZipping(true);
    try {
      const zip = new JSZip();
      for (const language of languages) {
        const data = await exportsApi.bundle(
          projectId,
          language,
          format,
          includeUnapproved,
        );
        zip.file(`${language}.json`, JSON.stringify(data, null, 2));
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      downloadBlob(blob, `${project.slug || 'project'}-translations.zip`);
    } catch (error) {
      setNotification({
        message: error instanceof Error ? error.message : 'ZIP export failed',
        type: 'error',
      });
    } finally {
      setZipping(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            API keys
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Client apps use these keys with the <code>x-api-key</code> header to
            fetch approved translations.
          </Typography>

          {newSecret?.key && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => dispatch(actions.clearNewApiKey())}
              action={
                <IconButton
                  size="small"
                  onClick={() => copy(newSecret.key as string)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Copy your new key now — it won&apos;t be shown again:
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
              >
                {newSecret.key}
              </Typography>
            </Alert>
          )}

          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <List dense>
              {apiKeys.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No API keys yet.
                </Typography>
              )}
              {apiKeys.map((key) => (
                <ListItem
                  key={key.id}
                  secondaryAction={
                    !key.revokedAt && (
                      <Tooltip title="Revoke">
                        <IconButton
                          edge="end"
                          onClick={() =>
                            dispatch(
                              actions.revokeApiKeyRequest({
                                projectId,
                                apiKeyId: key.id,
                              }),
                            )
                          }
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                >
                  <ListItemText
                    primary={`${key.name}${key.revokedAt ? ' (revoked)' : ''}`}
                    secondary={`Prefix: ${key.prefix}`}
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Divider sx={{ my: 2 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size="small"
              placeholder="Key name (e.g. Production web)"
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="contained"
              onClick={handleCreateKey}
              disabled={!keyName}
            >
              Create key
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Export bundle
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            sx={{ mb: 2 }}
          >
            <Select
              size="small"
              value={exportLanguage}
              onChange={(e) => setExportLanguage(e.target.value)}
              displayEmpty
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="" disabled>
                Language
              </MenuItem>
              {project?.targetLanguages.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={format}
              onChange={(e) => setFormat(e.target.value as 'nested' | 'flat')}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="nested">Nested</MenuItem>
              <MenuItem value="flat">Flat</MenuItem>
            </Select>
            <FormControlLabel
              control={
                <Switch
                  checked={includeUnapproved}
                  onChange={(e) => setIncludeUnapproved(e.target.checked)}
                />
              }
              label="Include unapproved"
            />
            <Button
              variant="contained"
              onClick={handleExport}
              disabled={!exportLanguage || exporting}
              startIcon={exporting ? <CircularProgress size={16} /> : null}
            >
              Generate
            </Button>
            <Button
              variant="outlined"
              onClick={handleDownloadZip}
              disabled={zipping}
              startIcon={
                zipping ? <CircularProgress size={16} /> : <FolderZipIcon />
              }
            >
              Download all (ZIP)
            </Button>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            component="p"
            sx={{ mb: 2 }}
          >
            The ZIP contains one file per language (e.g. en.json, ar.json),
            including the source language, using the selected format.
          </Typography>

          {bundle && (
            <Box>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => copy(bundle)}
                >
                  Copy
                </Button>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={downloadBundle}
                >
                  Download
                </Button>
              </Stack>
              <Box
                component="pre"
                sx={{
                  bgcolor: '#1e1e2e',
                  color: '#e0e0e0',
                  p: 2,
                  borderRadius: 2,
                  overflow: 'auto',
                  maxHeight: 360,
                  fontSize: '0.85rem',
                }}
              >
                {bundle}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
